import React, { useEffect, useState } from 'react';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';

/**
 * REFERRALS — who is referring whom, how often, and who to recruit next (Evan 2026-10-16,
 * ahead of the campaign featuring the signup referral-name entry).
 *
 * One definer RPC (admin_referrals_overview, admin_referrals_v1.sql) reads
 * profiles.referred_by_user_id/_username — the live capture from setup-profile. The `referrals`
 * points table stays out of this page until that system activates (it is empty today).
 * "Activated" everywhere = has at least one live rating.
 */

type Referee = { username: string | null; joined: string; ratings: number; city: string | null };
type Referrer = {
  referrer_id: string;
  referrer: string | null;
  referrer_city: string | null;
  count: number;
  activated: number;
  referees: Referee[];
};
type Rec = { username: string; city: string | null; university: string | null; friends: number; ratings: number };
type Overview = {
  totals: { referred_signups: number; matched: number; unmatched: number; activated: number };
  referrers: Referrer[];
  unmatched_entries: { typed: string; by: string | null; joined: string }[];
  by_source: { source: string; n: number }[];
  most_connected: Rec[];
  power_low_friends: Rec[];
};

export function ReferralsScreen() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openReferrer, setOpenReferrer] = useState<string | null>(null);

  useEffect(() => {
    void supabase.rpc('admin_referrals_overview').then(({ data: d, error: e }) => {
      if (e) {
        setError(
          /admin_referrals_overview/.test(e.message)
            ? 'Needs SQL applied: shado-admin/supabase/sql/admin_referrals_v1.sql'
            : e.message,
        );
        return;
      }
      setData(d as Overview);
    });
  }, []);

  return (
    <div className="admin-page mx-auto max-w-4xl">
      <PageHeader
        title="Referrals"
        subtitle="Who typed whose name at signup, how often it converts, and who to recruit for the campaign."
      />
      {error ? <div className="admin-banner-error mb-4">{error}</div> : null}
      {data === null && !error ? <SkeletonRows rows={8} /> : null}
      {data ? (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {(
              [
                ['Referred signups', data.totals.referred_signups],
                ['Matched to a real user', data.totals.matched],
                ['Unmatched names', data.totals.unmatched],
                ['Activated (rated ≥1)', data.totals.activated],
              ] as const
            ).map(([label, n]) => (
              <div key={label} className="rounded-xl border border-border bg-surface px-4 py-2.5">
                <div className="font-display text-[20px] font-semibold tabular-nums text-white">{n}</div>
                <div className="text-[10.5px] uppercase tracking-[0.08em] text-mutedDim">{label}</div>
              </div>
            ))}
            {data.by_source.map((s) => (
              <div key={s.source} className="rounded-xl border border-border/60 bg-surface px-4 py-2.5">
                <div className="font-display text-[20px] font-semibold tabular-nums text-muted">{s.n}</div>
                <div className="text-[10.5px] uppercase tracking-[0.08em] text-mutedDim">src: {s.source}</div>
              </div>
            ))}
          </div>

          <section>
            <div className="admin-eyebrow mb-2">Who referred who</div>
            {data.referrers.length === 0 ? (
              <EmptyState compact title="No referred signups yet" hint="Names typed at signup land here the moment they match a user." />
            ) : (
              <div className="space-y-2">
                {data.referrers.map((r) => {
                  const open = openReferrer === r.referrer_id;
                  return (
                    <div key={r.referrer_id} className="admin-card !p-0">
                      <button
                        type="button"
                        onClick={() => setOpenReferrer(open ? null : r.referrer_id)}
                        className="flex w-full flex-wrap items-center gap-3 px-4 py-2.5 text-left"
                      >
                        <span className="text-[13.5px] font-medium text-white">@{r.referrer ?? '—'}</span>
                        <span className="text-[11px] text-mutedDim">{r.referrer_city ?? ''}</span>
                        <span className="ml-auto rounded-full border border-accent/40 bg-accent/10 px-2.5 py-0.5 text-[11px] tabular-nums text-accent">
                          {r.count} referral{r.count === 1 ? '' : 's'}
                        </span>
                        <span className="text-[11px] tabular-nums text-emerald-300">{r.activated} activated</span>
                        <span className="text-[11px] text-mutedDim">{open ? '▴' : '▾'}</span>
                      </button>
                      {open ? (
                        <div className="space-y-1 border-t border-border/60 px-4 py-3">
                          {r.referees.map((ref, i) => (
                            <div key={`${ref.username}-${i}`} className="flex flex-wrap items-baseline gap-x-3 text-[12px]">
                              <span className="text-white">@{ref.username ?? '—'}</span>
                              <span className="text-mutedDim">joined {timeAgo(ref.joined)}</span>
                              <span className={ref.ratings > 0 ? 'text-emerald-300' : 'text-mutedDim'}>
                                {ref.ratings} rating{ref.ratings === 1 ? '' : 's'}
                              </span>
                              {ref.city ? <span className="text-mutedDim">{ref.city}</span> : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {data.unmatched_entries.length > 0 ? (
            <section>
              <div className="admin-eyebrow mb-2">Typed names that matched nobody</div>
              <div className="admin-card space-y-1">
                {data.unmatched_entries.map((u, i) => (
                  <div key={i} className="flex flex-wrap items-baseline gap-x-3 text-[12px]">
                    <span className="text-white">“{u.typed}”</span>
                    <span className="text-mutedDim">typed by @{u.by ?? '—'} · {timeAgo(u.joined)}</span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section className="grid gap-4 md:grid-cols-2">
            <div className="admin-card">
              <div className="admin-eyebrow mb-1">Campaign targets — most connected</div>
              <p className="mb-3 text-[11px] leading-relaxed text-mutedDim">
                The most mutual friends on Shado — one post from them reaches the most users.
              </p>
              <div className="space-y-1.5">
                {data.most_connected.map((u) => (
                  <div key={u.username} className="flex flex-wrap items-baseline gap-x-3 text-[12px]">
                    <span className="text-white">@{u.username}</span>
                    <span className="tabular-nums text-sky-300">{u.friends} friends</span>
                    <span className="tabular-nums text-mutedDim">{u.ratings} ratings</span>
                    <span className="text-mutedDim">{u.university ?? u.city ?? ''}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="admin-card">
              <div className="admin-eyebrow mb-1">Campaign targets — power users, few friends</div>
              <p className="mb-3 text-[11px] leading-relaxed text-mutedDim">
                5+ ratings but ≤2 friends — they love it alone; a referral nudge turns them into
                recruiters.
              </p>
              <div className="space-y-1.5">
                {data.power_low_friends.map((u) => (
                  <div key={u.username} className="flex flex-wrap items-baseline gap-x-3 text-[12px]">
                    <span className="text-white">@{u.username}</span>
                    <span className="tabular-nums text-[#b79cff]">{u.ratings} ratings</span>
                    <span className="tabular-nums text-mutedDim">{u.friends} friends</span>
                    <span className="text-mutedDim">{u.university ?? u.city ?? ''}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
